export type CharacterUgcRole = 'wife' | 'husband';

export interface CharacterUgcCharacter {
  role: CharacterUgcRole;
  name: string;
  appearance: string;
  voice: string;
  imageUrl?: string;
  imageFile?: string;
  imageSha256?: string;
}

export interface CharacterUgcConfig {
  version: 'couple-ugc-v1';
  characters: CharacterUgcCharacter[];
}

export interface CharacterUgcPortrait {
  imageUrl: string;
  imageSha256: string;
  imageFile?: string;
}

const MAX_PORTRAIT_BYTES = 5 * 1024 * 1024;
const ROLES: CharacterUgcRole[] = ['wife', 'husband'];

function invalid(message: string): never {
  throw new Error(`Character UGC: ${message}`);
}

function profileText(value: string, label: string, max: number): string {
  if (typeof value !== 'string' || !value.trim() || value.length > max || /[\x00-\x1f\x7f\u2028\u2029]/.test(value)) {
    invalid(`${label} must be nonempty single-line text (at most ${max} characters).`);
  }
  return value.trim().normalize('NFC');
}

function normalizedProfiles(config: CharacterUgcConfig): CharacterUgcConfig {
  if (!config || config.version !== 'couple-ugc-v1') invalid('Unsupported direction version.');
  if (!Array.isArray(config.characters) || config.characters.length !== 2) invalid('Exactly two character profiles are required.');
  const characters = config.characters.map(character => {
    if (!character || !ROLES.includes(character.role)) invalid('Only wife and husband profiles are supported.');
    return {
      role: character.role,
      name: profileText(character.name, `${character.role} name`, 80),
      appearance: profileText(character.appearance, `${character.role} appearance`, 600),
      voice: profileText(character.voice, `${character.role} voice`, 200),
      ...(character.imageUrl !== undefined ? { imageUrl: character.imageUrl } : {}),
      ...(character.imageFile !== undefined ? { imageFile: character.imageFile } : {}),
      ...(character.imageSha256 !== undefined ? { imageSha256: character.imageSha256 } : {}),
    };
  });
  if (new Set(characters.map(character => character.role)).size !== 2
    || new Set(characters.map(character => character.name.toLowerCase())).size !== 2) {
    invalid('Wife and husband must have different roles and names.');
  }
  return { version: 'couple-ugc-v1', characters: ROLES.map(role => characters.find(character => character.role === role)!) };
}

export async function validateCharacterPortrait(input: File | string): Promise<CharacterUgcPortrait> {
  let source: string;
  let imageFile: string | undefined;
  if (typeof input === 'string') {
    source = input;
  } else {
    if (typeof File === 'undefined' || !(input instanceof File)) invalid('Select a PNG, JPEG or WebP portrait.');
    if (input.size > MAX_PORTRAIT_BYTES) invalid('Portrait must be at most 5 MiB.');
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(input.type)) invalid('Select a PNG, JPEG or WebP portrait.');
    const bytes = new Uint8Array(await input.arrayBuffer());
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += 32768) {
      binary += String.fromCharCode(...bytes.subarray(offset, offset + 32768));
    }
    source = `data:${input.type};base64,${btoa(binary)}`;
    imageFile = input.name;
  }
  if (source.length > Math.ceil(MAX_PORTRAIT_BYTES / 3) * 4 + 32) invalid('Portrait must be at most 5 MiB.');
  const header = /^data:(image\/(?:png|jpeg|webp));base64,/i.exec(source);
  if (!header) invalid('Portrait must be a PNG, JPEG or WebP data URL; external URLs and SVG are not supported.');
  const payload = source.slice(header[0].length);
  if (!payload || payload.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(payload)) invalid('Portrait has invalid base64 data.');
  const decodedSize = payload.length / 4 * 3 - (payload.endsWith('==') ? 2 : payload.endsWith('=') ? 1 : 0);
  if (decodedSize > MAX_PORTRAIT_BYTES) invalid('Portrait must be at most 5 MiB.');
  let binary: string;
  try {
    binary = atob(payload);
  } catch {
    invalid('Portrait has invalid base64 data.');
  }
  const mime = header[1].toLowerCase();
  const signatureMatches = mime === 'image/png'
    ? binary.startsWith('\x89PNG\r\n\x1a\n')
    : mime === 'image/jpeg'
      ? binary.startsWith('\xff\xd8\xff')
      : binary.startsWith('RIFF') && binary.slice(8, 12) === 'WEBP';
  if (!signatureMatches) invalid('Portrait contents do not match the PNG, JPEG or WebP file type.');
  const imageUrl = `data:${mime};base64,${btoa(binary)}`;
  if (!globalThis.crypto?.subtle) invalid('Portrait hashing requires a secure browser context (HTTPS or localhost).');
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(imageUrl));
  const imageSha256 = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  return { imageUrl, imageSha256, ...(imageFile !== undefined ? { imageFile } : {}) };
}

export async function createCharacterUgcSnapshot(config: CharacterUgcConfig): Promise<CharacterUgcConfig> {
  const snapshot = normalizedProfiles(config);
  for (const character of snapshot.characters) {
    if (!character.imageUrl) invalid(`Attach the original ${character.role} portrait before queuing.`);
  }
  snapshot.characters = await Promise.all(snapshot.characters.map(async character => ({
    ...character,
    ...await validateCharacterPortrait(character.imageUrl!),
  })));
  return snapshot;
}

export function stripCharacterUgcImages(config: CharacterUgcConfig): CharacterUgcConfig {
  return { ...config, characters: config.characters.map(({ imageUrl: _imageUrl, ...character }) => character) };
}

export async function reattachCharacterReference(config: CharacterUgcConfig, role: CharacterUgcRole, input: File | string): Promise<CharacterUgcConfig> {
  normalizedProfiles(config);
  const snapshot = { ...config, characters: config.characters.map(character => ({ ...character })) };
  const character = snapshot.characters.find(entry => entry.role === role);
  if (!character?.imageSha256 || !/^[a-f0-9]{64}$/.test(character.imageSha256)) invalid(`The saved ${role} portrait hash is missing. Queue a new job with both originals.`);
  const portrait = await validateCharacterPortrait(input);
  if (portrait.imageSha256 !== character.imageSha256) invalid(`This portrait does not match the original ${role} reference. Select the original file.`);
  Object.assign(character, portrait);
  return snapshot;
}